import React, { useState, useEffect, useRef } from 'react'; // Ajout de useRef
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import '../styles/icons.css';
import Breadcrumb from './Breadcrumb';
import Leads from './Leads';
import Clients from './Clients';
import Facturation from './Facturation';
import RendezVous from './RendezVous';
import Charges from './Charges';
import NotificationDropdown from './notifications/NotificationDropdown';
import LeadFollowupAlerts from './LeadFollowupAlerts';
import AdministrationPanel from './AdministrationPanel';
import TableauBord from './TableauBord';
import Profile from './Profile';
// import ModuleRenderer from './ModuleRenderer'; // Commentez ou supprimez cette ligne
import Procedures from './Procedures';
import VisaDocumentGenerator from './VisaDocumentGenerator';
import ClientEdit from './ClientEdit';
import Rapports from './Rapports'; // Import du nouveau composant Rapports
import MesTachesPage from './MesTachesPage'; // Import du composant Mes Tâches
import GlobalSearch from './GlobalSearch'; // Import du composant de recherche globale
import ImmigrationCalculator from './ImmigrationCalculator'; // Import du calculateur d'admissibilité
import PSUQCalculator from './PSUQCalculator'; // Import du calculateur PSTQ Québec
// @ts-ignore
import EntreeExpressCalculator from './EntreeExpressCalculator'; // Import du calculateur Entrée Express
import { useNotifications } from '../contexts/NotificationContext'; // Ajout de l'import
import { useSettings } from '../contexts/SettingsContext'; // Import du contexte des paramètres
import { getMyProfile } from '../services/usersAPI'; // Import pour récupérer le profil
import settingsService from '../services/settingsService'; // Import du service des paramètres
import { ToastProvider } from './notifications/ToastProvider'; // Import du ToastProvider pour les notifications modernes
import { buildImageUrl } from '../utils/urlHelpers';

// Fonction utilitaire pour construire l'URL complète des images
const DEFAULT_AVATAR = '/default-avatar.svg';

const getImageUrl = (imagePath) => buildImageUrl(imagePath, { defaultPath: DEFAULT_AVATAR });

function Dashboard({ userRole = 'directeur', userName = 'Utilisateur Test', entrepriseName = '', onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isNestedClientPage = location.pathname.startsWith('/client/nouveau') || location.pathname.startsWith('/client/modifier/');
  // Définir les modules accessibles selon le rôle
  const getAccessibleModules = (role) => {
    const modules = {
      'administrateur': ['tableau-bord', 'leads', 'clients', 'facturation', 'rendez-vous', 'charges', 'mes-taches', 'rapports', 'administration', 'admissibilite', 'entree-express', 'pstq-quebec', 'procedures', 'documents'],
      // Le super-administrateur garde tous les accès de l'administrateur.
      'super_admin': ['tableau-bord', 'leads', 'clients', 'facturation', 'rendez-vous', 'charges', 'mes-taches', 'rapports', 'administration', 'admissibilite', 'entree-express', 'pstq-quebec', 'procedures', 'documents'],
      'manager': ['tableau-bord', 'leads', 'clients', 'facturation', 'rendez-vous', 'charges', 'mes-taches', 'rapports', 'admissibilite', 'entree-express', 'pstq-quebec', 'procedures', 'documents'],
      'directeur': ['tableau-bord', 'mes-taches', 'leads', 'clients', 'facturation', 'rendez-vous', 'charges', 'administration', 'admissibilite', 'entree-express', 'pstq-quebec', 'procedures', 'documents', 'rapports'],
      'conseillere': ['tableau-bord', 'mes-taches', 'leads', 'clients', 'facturation', 'rendez-vous', 'admissibilite', 'entree-express', 'pstq-quebec', 'procedures', 'documents'],
      'comptable': ['tableau-bord', 'mes-taches', 'facturation', 'charges'],
      'secretaire': ['tableau-bord', 'mes-taches', 'leads', 'clients', 'facturation', 'rendez-vous', 'admissibilite', 'entree-express', 'pstq-quebec', 'procedures', 'documents']
    };
    
    return modules[role] || ['tableau-bord'];
  };

  const accessibleModules = getAccessibleModules(userRole);

  // 🔄 Mémoriser le module actif dans localStorage pour le restaurer après un rafraîchissement
  const [activeModule, setActiveModule] = useState(() => {
    const savedModule = localStorage.getItem('lastActiveModule');
    // Si un module est sauvegardé ET accessible, l'utiliser
    if (savedModule && accessibleModules.includes(savedModule)) {
      return savedModule;
    }
    // Sinon, utiliser 'tableau-bord' par défaut
    return 'tableau-bord';
  });
  const [showProfileMenu, setShowProfileMenu] = useState(false); // Menu sidebar (bas)
  const [showHeaderProfileMenu, setShowHeaderProfileMenu] = useState(false); // Menu header (haut)
  const [userPhoto, setUserPhoto] = useState(DEFAULT_AVATAR);
  const [navigationParams, setNavigationParams] = useState({}); // Paramètres de navigation (ex: filtres)
  const { removeNotification } = useNotifications(); // Utilisation du contexte notifications
  const { settings } = useSettings(); // Utilisation du contexte des paramètres
  const dashboardRef = useRef(null); // Référence pour le conteneur principal
  const profileMenuRef = useRef(null); // Référence pour le menu profil header
  const sidebarProfileMenuRef = useRef(null); // Référence pour le menu profil sidebar

  // Charger la photo de profil au montage du composant
  useEffect(() => {
    const loadUserPhoto = async () => {
      try {
        const profileData = await getMyProfile();
        if (profileData.success && profileData.data && profileData.data.photo) {
          setUserPhoto(profileData.data.photo);
        }
      } catch (error) {
        console.log('Info: Impossible de charger la photo de profil, utilisation de l\'avatar par défaut');
      }
    };

    loadUserPhoto();
  }, []);

  // Fermer les menus profil quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Fermer le menu header si on clique ailleurs
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowHeaderProfileMenu(false);
      }
      // Fermer le menu sidebar si on clique ailleurs
      if (sidebarProfileMenuRef.current && !sidebarProfileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showHeaderProfileMenu || showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHeaderProfileMenu, showProfileMenu]);

  // État pour l'édition de client
  const [clientToEdit, setClientToEdit] = useState(null);
  
  // Formater le rôle pour l'affichage
  const formaterRole = (role) => {
    const roles = {
      'administrateur': 'Administrateur',
      'super_admin': 'Super-administrateur',
      'directeur': 'Directeur',
      'conseillere': 'Conseillère',
      'comptable': 'Comptable',
      'secretaire': 'Secrétaire'
    };
    return roles[role] || role;
  };
  
  // 🔄 Fonction utilitaire pour changer de module et mémoriser le choix
  const changeActiveModule = (module) => {
    setActiveModule(module);
    // ✅ Sauvegarder dans localStorage pour restaurer après un rafraîchissement
    localStorage.setItem('lastActiveModule', module);
    // Assurer la sortie des routes imbriquées (ex: /client/nouveau) pour réactiver le contenu modules
    if (location.pathname !== '/') {
      navigate('/');
    }
  };
  
  // Gérer le clic sur un module
  const handleModuleClick = (module, params = {}) => {
    console.log(`Navigation vers module: ${module}`, params);
    
    // Stocker les paramètres de navigation
    if (Object.keys(params).length > 0) {
      setNavigationParams(params);
    } else {
      // Réinitialiser les paramètres si navigation manuelle
      setNavigationParams({});
    }
    
    changeActiveModule(module);
    // Réinitialiser l'état d'édition de client si on change de module
    if (module !== 'client-edit') {
      setClientToEdit(null);
    }
  };
  
  // Fonction pour naviguer vers un module avec des paramètres optionnels
  const handleNavigateFromSearch = (module, params = {}) => {
    changeActiveModule(module);
    
    // Si des paramètres sont fournis, on peut les traiter ici
    // Par exemple, mettre en surbrillance un élément spécifique
    if (params.highlightId) {
      // On peut stocker l'ID à mettre en surbrillance dans l'état
      // ou utiliser localStorage/sessionStorage pour le passer au composant
      sessionStorage.setItem('highlightId', params.highlightId);
    }
  };
  
  // Fonction pour éditer un client
  const handleEditClient = (clientId) => {
    setClientToEdit(clientId);
    changeActiveModule('client-edit');
  };
  
  // Basculer l'affichage du menu profil SIDEBAR (bas)
  const toggleProfileMenu = (e) => {
    e.stopPropagation(); // Empêcher la propagation du clic
    setShowProfileMenu(prev => !prev);
    setShowHeaderProfileMenu(false); // Fermer l'autre menu
  };

  // Basculer l'affichage du menu profil HEADER (haut)
  const toggleHeaderProfileMenu = (e) => {
    e.stopPropagation(); // Empêcher la propagation du clic
    setShowHeaderProfileMenu(prev => !prev);
    setShowProfileMenu(false); // Fermer l'autre menu
  };

  // Fonction pour mettre à jour la photo de profil depuis le composant Profile
  const updateUserPhoto = (newPhotoUrl) => {
    setUserPhoto(newPhotoUrl);
  };

  // Afficher la page de profil depuis SIDEBAR
  const showProfile = (e) => {
    if (e) e.stopPropagation(); // Empêcher la propagation du clic
    changeActiveModule('profile');
    setShowProfileMenu(false);
  };

  // Afficher la page de profil depuis HEADER
  const showProfileFromHeader = (e) => {
    if (e) e.stopPropagation(); // Empêcher la propagation du clic
    changeActiveModule('profile');
    setShowHeaderProfileMenu(false);
  };

  // Gérer la déconnexion depuis SIDEBAR
  const handleLogout = (e) => {
    if (e) e.stopPropagation(); // Empêcher la propagation du clic
    setShowProfileMenu(false);
    // Réinitialiser le module actif à 'tableau-bord' lors de la déconnexion
    localStorage.setItem('lastActiveModule', 'tableau-bord');
    onLogout();
  };

  // Gérer la déconnexion depuis HEADER
  const handleLogoutFromHeader = (e) => {
    if (e) e.stopPropagation(); // Empêcher la propagation du clic
    setShowHeaderProfileMenu(false);
    // Réinitialiser le module actif à 'tableau-bord' lors de la déconnexion
    localStorage.setItem('lastActiveModule', 'tableau-bord');
    onLogout();
  };

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Raccourcis avec Alt
      if (e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'h': // Alt+H pour l'accueil
            if (accessibleModules.includes('tableau-bord')) changeActiveModule('tableau-bord');
            break;
          case 't': // Alt+T pour les tâches
            if (accessibleModules.includes('mes-taches')) changeActiveModule('mes-taches');
            break;
          case 'l': // Alt+L pour les leads
            if (accessibleModules.includes('leads')) changeActiveModule('leads');
            break;
          case 'c': // Alt+C pour les clients
            if (accessibleModules.includes('clients')) changeActiveModule('clients');
            break;
          case 'f': // Alt+F pour la facturation
            if (accessibleModules.includes('facturation')) changeActiveModule('facturation');
            break;
          case 'r': // Alt+R pour les rendez-vous
            if (accessibleModules.includes('rendez-vous')) changeActiveModule('rendez-vous');
            break;
          case 'p': // Alt+P pour le profil
            showProfile();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [accessibleModules]); // Dépendances du useEffect

  // Écouter les événements de changement de module depuis les composants enfants
  useEffect(() => {
    const handleModuleChange = (event) => {
      const targetModule = event.detail;
      if (accessibleModules.includes(targetModule)) {
        changeActiveModule(targetModule);
      }
    };

    window.addEventListener('changeModule', handleModuleChange);
    return () => {
      window.removeEventListener('changeModule', handleModuleChange);
    };
  }, [accessibleModules]);

  // Générer les éléments du fil d'Ariane
  const getBreadcrumbItems = () => {
    const items = [
      { label: 'Accueil', onClick: () => changeActiveModule('tableau-bord') }
    ];
    
    switch (activeModule) {
      case 'leads':
        items.push({ label: 'Gestion des leads' });
        break;
      case 'clients':
        items.push({ label: 'Gestion des clients' });
        break;
      case 'procedures':
        items.push({ label: 'Procédures' });
        break;
      case 'documents':
        items.push({ label: 'Documents' });
        break;
      case 'facturation':
        items.push({ label: 'Facturation' });
        break;
      case 'rendez-vous':
        items.push({ label: 'Rendez-vous' });
        break;
      case 'charges':
        items.push({ label: 'Gestion des charges' });
        break;
      case 'mes-taches':
        items.push({ label: 'Mes Tâches' });
        break;
      case 'administration':
        items.push({ label: 'Administration' });
        break;
      case 'admissibilite':
        items.push({ label: 'Calculateur d\'Admissibilité' });
        break;
      case 'entree-express':
        items.push({ label: 'Calculateur Entrée Express' });
        break;
      case 'pstq-quebec':
        items.push({ label: 'Calculateur PSTQ Québec' });
        break;
      case 'rapports':
        items.push({ label: 'Rapports d\'avancement' });
        break;
      case 'profile':
        items.push({ label: 'Mon Profil' });
        break;
      default:
        break;
    }
    
    return items;
  };

  return (
    <ToastProvider position="top-right">
      <div className="dashboard-container">
        <div className="sidebar">
          <div className="logo">
            <div className="logo-mark">
              <img
                src={settingsService.getImageUrl(settings.logo || '/assets/logo.png')}
                alt={`Logo ${settings.nom}`}
                onError={(e) => {
                  e.target.src = '/assets/logo.png';
                }}
              />
            </div>
            <div className="logo-copy">
              <h1>{settings.nom}</h1>
              <span>CRM immigration</span>
            </div>
          </div>
        
        <nav className="sidebar-nav">
          <ul>
            {accessibleModules.includes('tableau-bord') && (
              <li 
                className={activeModule === 'tableau-bord' ? 'active' : ''}
                onClick={() => handleModuleClick('tableau-bord')}
              >
                <i className="fas fa-chart-line"></i>
                <span>Tableau de bord</span>
              </li>
            )}
            
            {accessibleModules.includes('leads') && (
              <li 
                className={activeModule === 'leads' ? 'active' : ''}
                onClick={() => handleModuleClick('leads')}
              >
                <i className="fas fa-funnel-dollar"></i>
                <span>Leads</span>
              </li>
            )}
            
            {accessibleModules.includes('clients') && (
              <li 
                className={activeModule === 'clients' ? 'active' : ''}
                onClick={() => handleModuleClick('clients')}
              >
                <i className="fas fa-users"></i>
                <span>Clients</span>
              </li>
            )}
            
            {accessibleModules.includes('facturation') && (
              <li 
                className={activeModule === 'facturation' ? 'active' : ''}
                onClick={() => handleModuleClick('facturation')}
              >
                <i className="fas fa-file-invoice-dollar"></i>
                <span>Facturation</span>
              </li>
            )}
            
            {accessibleModules.includes('rendez-vous') && (
              <li 
                className={activeModule === 'rendez-vous' ? 'active' : ''}
                onClick={() => handleModuleClick('rendez-vous')}
              >
                <i className="fas fa-calendar-alt"></i>
                <span>Rendez-vous</span>
              </li>
            )}
            
            {accessibleModules.includes('charges') && (
              <li 
                className={activeModule === 'charges' ? 'active' : ''}
                onClick={() => handleModuleClick('charges')}
              >
                <i className="fas fa-money-bill-wave"></i>
                <span>Charges</span>
              </li>
            )}
            
            {accessibleModules.includes('mes-taches') && (
              <li 
                className={activeModule === 'mes-taches' ? 'active' : ''}
                onClick={() => handleModuleClick('mes-taches')}
              >
                <i className="fas fa-clipboard-list"></i>
                <span>Mes Tâches</span>
              </li>
            )}
            
            {accessibleModules.includes('rapports') && (
              <li 
                className={activeModule === 'rapports' ? 'active' : ''}
                onClick={() => handleModuleClick('rapports')}
              >
                <i className="fas fa-file-alt"></i>
                <span>Rapports</span>
              </li>
            )}
            
            {accessibleModules.includes('administration') && (
              <li 
                className={activeModule === 'administration' ? 'active' : ''}
                onClick={() => handleModuleClick('administration')}
              >
                <i className="fas fa-cogs"></i>
                <span>Administration</span>
              </li>
            )}
            
            {accessibleModules.includes('admissibilite') && (
              <li 
                className={activeModule === 'admissibilite' ? 'active' : ''}
                onClick={() => handleModuleClick('admissibilite')}
              >
                <i className="fas fa-calculator"></i>
                <span>Admissibilité</span>
              </li>
            )}

            {accessibleModules.includes('entree-express') && (
              <li 
                className={activeModule === 'entree-express' ? 'active' : ''}
                onClick={() => handleModuleClick('entree-express')}
              >
                <i className="fas fa-calculator"></i>
                <span>Entrée Express</span>
              </li>
            )}

            {accessibleModules.includes('pstq-quebec') && (
              <li 
                className={activeModule === 'pstq-quebec' ? 'active' : ''}
                onClick={() => handleModuleClick('pstq-quebec')}
              >
                <i className="fas fa-calculator"></i>
                <span>PSTQ Québec</span>
              </li>
            )}
            
            {accessibleModules.includes('procedures') && (
              <li 
                className={activeModule === 'procedures' ? 'active' : ''}
                onClick={() => handleModuleClick('procedures')}
              >
                <i className="fas fa-tasks"></i>
                <span>Procédures</span>
              </li>
            )}
            
            {accessibleModules.includes('documents') && (
              <li 
                className={activeModule === 'documents' ? 'active' : ''}
                onClick={() => handleModuleClick('documents')}
              >
                <i className="fas fa-file-text"></i>
                <span>Documents</span>
              </li>
            )}
          </ul>
        </nav>
        
        <div className="user-profile" onClick={toggleProfileMenu} ref={sidebarProfileMenuRef}>
          <div className="user-avatar">
            <img src={getImageUrl(userPhoto)} alt="Avatar" />
          </div>
          <div className="user-info">
            <h3>{userName}</h3>
            <p>{formaterRole(userRole)}</p>
            {entrepriseName && <p className="user-company">{entrepriseName}</p>}
          </div>
          <i className={`fas fa-chevron-${showProfileMenu ? 'up' : 'down'}`}></i>
          
          {showProfileMenu && (
            <div className="profile-dropdown">
              <ul>
                <li onClick={showProfile}>
                  <i className="fas fa-user"></i>
                  <span>Mon Profil</span>
                </li>
                <li onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Déconnexion</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      
      <div className="main-content">
        <header className="dashboard-header">
          <div className="header-title">
            <span className="header-eyebrow">Espace de travail</span>
            <h2>
              {activeModule === 'tableau-bord' && 'Tableau de bord'}
              {activeModule === 'mes-taches' && 'Mes Tâches'}
              {activeModule === 'leads' && 'Gestion des leads'}
              {activeModule === 'clients' && 'Gestion des clients'}
              {activeModule === 'procedures' && 'Procédures'}
              {activeModule === 'documents' && 'Générateur de Documents'}
              {activeModule === 'facturation' && 'Facturation'}
              {activeModule === 'rendez-vous' && 'Rendez-vous'}
              {activeModule === 'charges' && 'Gestion des charges'}
              {activeModule === 'rapports' && 'Rapports d\'avancement'}
              {activeModule === 'administration' && 'Administration'}
              {activeModule === 'admissibilite' && 'Calculateur d\'Admissibilité'}
              {activeModule === 'pstq-quebec' && 'Calculateur PSTQ Québec'}
              {activeModule === 'entree-express' && 'Calculateur Entrée Express'}
              {activeModule === 'profile' && 'Mon Profil'}
            </h2>
          </div>
          
          <div className="header-actions">
            <NotificationDropdown />
            <div className="search-global">
              <GlobalSearch onNavigate={handleNavigateFromSearch} />
            </div>
            <div className="user-header-info">
              <div className="user-header-copy">
                <span className="user-name">{userName}</span>
                <span className="user-role">{formaterRole(userRole)}</span>
                {entrepriseName && <span className="user-company">{entrepriseName}</span>}
              </div>
              <div className="user-avatar-small" onClick={toggleHeaderProfileMenu} ref={profileMenuRef}>
                <img src={getImageUrl(userPhoto)} alt="Avatar" />
                <i className={`fas fa-chevron-${showHeaderProfileMenu ? 'up' : 'down'}`}></i>
                
                {showHeaderProfileMenu && (
                  <div className="header-profile-dropdown">
                    <ul>
                      <li onClick={showProfileFromHeader}>
                        <i className="fas fa-user"></i>
                        <span>Mon Profil</span>
                      </li>
                      <li onClick={handleLogoutFromHeader}>
                        <i className="fas fa-sign-out-alt"></i>
                        <span>Déconnexion</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        
        <div className="module-content">
          <LeadFollowupAlerts userRole={userRole} onOpenLead={id => handleModuleClick('leads', { ouvrirDetailLead: id })} />
          <div className="breadcrumb-container">
            <Breadcrumb items={getBreadcrumbItems()} />
          </div>
          
          {/* Si une route imbriquée (ajout/modification client) est active, on l'affiche ici via Outlet */}
          {isNestedClientPage ? (
            <Outlet />
          ) : (
            <>
              {activeModule === 'tableau-bord' && <TableauBord userRole={userRole} userName={userName} onNavigate={handleModuleClick} />}
              {activeModule === 'mes-taches' && <MesTachesPage userRole={userRole} userName={userName} navigationParams={navigationParams} />}
              {activeModule === 'leads' && <Leads userRole={userRole} navigationParams={navigationParams} onNavigate={handleModuleClick} />}
              {activeModule === 'clients' && <Clients onEditClient={handleEditClient} userRole={userRole} />}
              {activeModule === 'client-edit' && <ClientEdit id={clientToEdit} onBack={() => changeActiveModule('clients')} />}
              {activeModule === 'procedures' && <Procedures userRole={userRole} />}
              {activeModule === 'documents' && <VisaDocumentGenerator />}
              {activeModule === 'facturation' && <Facturation userRole={userRole} navigationParams={navigationParams} />}
              {activeModule === 'rendez-vous' && <RendezVous userRole={userRole} onNavigate={handleModuleClick} navigationParams={navigationParams} />}
              {activeModule === 'charges' && <Charges userRole={userRole} />}
              {activeModule === 'rapports' && <Rapports userRole={userRole} />}
              {activeModule === 'administration' && <AdministrationPanel userRole={userRole} />}
              {activeModule === 'admissibilite' && <ImmigrationCalculator />}
              {activeModule === 'entree-express' && <EntreeExpressCalculator />}
              {activeModule === 'pstq-quebec' && <PSUQCalculator />}
              {activeModule === 'profile' && <Profile userRole={userRole} userName={userName} onPhotoUpdate={updateUserPhoto} />}
            </>
          )}
        </div>
      </div>
    </div>
    </ToastProvider>
  );
}

export default Dashboard;
